/* eslint-disable import/no-deprecated */
import moment, { Moment } from 'moment';
import React, { ReactNode, useMemo } from 'react';
import { Typography } from '../../../deprecated/Typography';
import { Box } from '../../Box';
import { Day } from '../Day';
import { MomentRange, MomentRanges, RangeIndex } from '../types';
import css from './index.module.scss';
import { daysInMonth } from './util';

export interface MonthProps {
  /**
   * The month that will be rendered, must be a Moment object.
   */
  month: Moment;
  /**
   * The selected/comparison date ranges. The `Day` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Controls the color of the preview range and which range takes priority when
   * rendering overlapping ranges.
   */
  focusedRange: RangeIndex;
  /**
   * Handler that's called when user clicks a day.
   */
  onDayClick: (day: Moment) => void;
  /**
   * Handler that's called when user hovers over a day.
   */
  onDayHover: (day: Moment | null) => void;
  /**
   * Outlined range used to provide a selection preview while selecting a range.
   * Color of the preview outline is determined by which range is currently
   * focused.
   */
  preview?: MomentRange;
  /**
   * The minimum selectable date. If provided, days before this date will be disabled.
   */
  minDate?: Moment;
  /**
   * The maximum selectable date. If provided, days after this date will be disabled.
   */
  maxDate?: Moment;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Moment) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Moment) => ReactNode;
}

const Month: React.FC<MonthProps> = ({
  month,
  ranges,
  focusedRange,
  preview,
  minDate,
  maxDate,
  isDayDisabled,
  renderInfoTooltip,
  onDayClick,
  onDayHover,
}) => {
  const days = useMemo(() => daysInMonth(month), [month]);
  const dayOffset = moment(month).startOf('month').day();
  return (
    <Box data-component="Month" testId="date-range-picker-month">
      <Box marginLeft={12} marginRight={12} marginBottom={8}>
        <Typography variant="body2">{month.format('MMMM YYYY')}</Typography>
      </Box>
      <Box className={css.dayGrid}>
        {dayOffset > 0 && <Box style={{ gridColumnStart: dayOffset }} />}
        {days.map(day => {
          const isOutsideMinMax =
            (minDate && day.isBefore(minDate, 'day')) || (maxDate && day.isAfter(maxDate, 'day'));
          return (
            <Day
              key={day.toISOString()}
              day={day}
              ranges={ranges}
              focusedRange={focusedRange}
              preview={preview}
              onClick={() => onDayClick(day)}
              onMouseEnter={() => onDayHover(day)}
              onMouseLeave={() => onDayHover(null)}
              disabled={isOutsideMinMax || isDayDisabled?.(day)}
              infoTooltip={renderInfoTooltip?.(day)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const MemoizedMonth = React.memo(Month);

export { MemoizedMonth as Month };
